import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Pencil, UserX, Shield, Users, GraduationCap, Building2, Globe, Network, UserCheck, MessageCircle, Mail, UserPlus, Check, FileText, Trash2, Eye, AlertTriangle, ExternalLink, HelpCircle, Clock, CheckCircle2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { DataTable } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal, PasswordConfirmModal } from '../../components/ui';
import { InviteResultDialog, InviteStatusCard } from '../../components/invite';
import { ROUTES } from '../../api/routes';
import type { Usuario } from '../../types/models';
import { useAuthStore } from '../../store/authStore';
import { useDevStore } from '../../store/useDevStore';
import { DocumentRedactorModal } from '../../components/ui/DocumentRedactorModal';
import { UsuariosSenhaTab } from './usuarios/UsuariosSenhaTab';
import { UsuariosPermissoesTab } from './usuarios/UsuariosPermissoesTab';
import { AlunoFichaDesktop } from '../admin/AlunoFichaDesktop';
import { ResponsavelFichaDesktop } from '../admin/ResponsavelFichaDesktop';
import { CadastroEscolarInfoModal } from '../../components/gestao/CadastroEscolarInfoModal';
import { UserAvatar } from '../../components/ui/UserAvatar';
import '../../components/ui/datatable.css';

const formatCPF = (val: string) => {
  const clean = val.replace(/\D/g, '');
  if (clean.length <= 3) return clean;
  if (clean.length <= 6) return `${clean.slice(0, 3)}.${clean.slice(3)}`;
  if (clean.length <= 9) return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6)}`;
  return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9, 11)}`;
};

const formatPhone = (val: string) => {
  const clean = val.replace(/\D/g, '');
  if (clean.length <= 2) return clean;
  if (clean.length <= 7) return `(${clean.slice(0, 2)}) ${clean.slice(2)}`;
  return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7, 11)}`;
};

function SearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Selecione...'
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: { id: string; nome: string }[];
  placeholder?: string;
}) {
  const [filterText, setFilterText] = useState('');

  const filteredOptions = useMemo(() => {
    return (options || []).filter(opt =>
      (opt.nome || '').toLowerCase().includes(filterText.toLowerCase())
    );
  }, [options, filterText]);

  return (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label className="form-label">{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input
          className="form-input"
          style={{ padding: '6px 10px', fontSize: '0.8rem' }}
          placeholder={`🔎 Filtrar ${label.toLowerCase()}...`}
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
        />
        <select
          className="form-select"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <option value="">{placeholder}</option>
          {filteredOptions.map(opt => (
            <option key={opt.id} value={opt.id}>{opt.nome}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/** Perfis que o Master pode criar diretamente. */
const PERFIS_CRIAVEIS = ['ADMIN', 'REDE', 'ESTADUAL', 'MUNICIPAL', 'ESCOLA', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'] as const;
const CARGOS_ESCOLA = ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA'] as const;
const VALIDOS_PERFIS = ['ADMIN', 'REDE', 'ESTADUAL', 'MUNICIPAL', 'ESCOLA', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'];

const PERFIL_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  ADMIN: { label: 'ADMIN', color: '#ef4444', icon: Shield },
  REDE: { label: 'REDE', color: '#8b5cf6', icon: Network },
  ESTADUAL: { label: 'ESTADUAL', color: '#6366f1', icon: Globe },
  MUNICIPAL: { label: 'MUNICIPAL', color: '#3b82f6', icon: Globe },
  DIRETOR: { label: 'DIRETOR', color: '#10b981', icon: Building2 },
  VICE_DIRETOR: { label: 'VICE DIRETOR', color: '#059669', icon: Building2 },
  COORDENACAO: { label: 'COORDENAÇÃO', color: '#14b8a6', icon: Building2 },
  SECRETARIA: { label: 'SECRETARIA', color: '#0ea5e9', icon: Building2 },
  ESCOLA: { label: 'ESCOLA', color: '#10b981', icon: Building2 },
  PROFESSOR: { label: 'PROFESSOR', color: '#f59e0b', icon: GraduationCap },
  RESPONSAVEL: { label: 'RESPONSÁVEL', color: '#ec4899', icon: UserCheck },
  ALUNO: { label: 'ALUNO', color: '#0891b2', icon: Users },
};

/** Ordem fixa dos filtros */
const FILTRO_ORDER = ['ADMIN', 'REDE', 'ESTADUAL', 'MUNICIPAL', 'DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'];

/**
 * Normaliza cargos escolares legados e rótulos amigáveis para o filtro Master.
 * O banco historicamente recebeu valores como "Coordenador(a)".
 */
const normalizeCargoFiltro = (cargo?: string | null): string => {
  const normalized = (cargo || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (normalized.includes('VICE')) return 'VICE_DIRETOR';
  if (normalized.includes('DIRETOR')) return 'DIRETOR';
  if (normalized.includes('COORDEN') || normalized.includes('SUPERVIS') || normalized.includes('ORIENTAD')) return 'COORDENACAO';
  if (normalized.includes('SECRETAR')) return 'SECRETARIA';
  return normalized;
};

const matchesCargoFiltro = (u: Usuario, cargo: string): boolean => {
  if (u.perfil !== 'ESCOLA') return false;
  const rawCargo = (u as any).cargo as string | null | undefined;
  // Usuários escolares antigos sem cargo eram tratados como direção.
  if (cargo === 'DIRETOR' && !rawCargo) return true;
  return normalizeCargoFiltro(rawCargo) === cargo;
};

/* gerarSenha importado de ../../components/invite */

export function UsuariosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, update, deactivate, refetch } = useCrud<Usuario>('admin/usuarios');
  const currentUser = useAuthStore((s) => s.user);
  const devSettings = useDevStore((s) => s.settings);
  const [auditUser, setAuditUser] = useState<Usuario | null>(null);

  const [docFrenteBase64, setDocFrenteBase64] = useState<string>('');
  const [docVersoBase64, setDocVersoBase64] = useState<string>('');

  const [redactorFile, setRedactorFile] = useState<File | null>(null);
  const [redactorTitle, setRedactorTitle] = useState<'Frente' | 'Verso'>('Frente');

  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionInput, setShowRejectionInput] = useState(false);
  const [submittingDoc, setSubmittingDoc] = useState(false);

  useEffect(() => {
    if (auditUser) {
      setDocFrenteBase64(auditUser.documentoFrente || '');
      setDocVersoBase64(auditUser.documentoVerso || '');
      setRejectionReason('');
      setShowRejectionInput(false);
    } else {
      setDocFrenteBase64('');
      setDocVersoBase64('');
      setRejectionReason('');
      setShowRejectionInput(false);
    }
  }, [auditUser]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Usuario | null>(null);
  const [detailUser, setDetailUser] = useState<Usuario | null>(null);
  const [form, setForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    perfil: 'ESCOLA',
    cargo: 'DIRETOR',
    cpf: '',
    dataNascimento: '',
    redeId: '',
    estadoId: '',
    municipioId: '',
    escolaId: ''
  });
  const [step, setStep] = useState(1);
  const [editTab, setEditTab] = useState<'dados' | 'permissoes' | 'senha'>('dados');
  const [saving, setSaving] = useState(false);
  const [filtro, setFiltro] = useState<string>('TODOS');
  const [statusFiltro, setStatusFiltro] = useState<'TODOS' | 'ATIVO' | 'AGUARDANDO' | 'EXPIRADO' | 'CADASTRO' | 'DESATIVADO'>('TODOS');
  const [exibirDemo, setExibirDemo] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [waDialog, setWaDialog] = useState<{ nome: string; telefone: string } | null>(null);
  const [waMsg, setWaMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<Usuario | null>(null);
  const [userToHardDelete, setUserToHardDelete] = useState<Usuario | null>(null);
  const [inviteResult, setInviteResult] = useState<{ activationLink: string; whatsappLink: string | null; emailSent: boolean; userName: string } | null>(null);
  const [cpfError, setCpfError] = useState<string | null>(null);
  const [cpfWarning, setCpfWarning] = useState<string | null>(null);
  const [checkingCpf, setCheckingCpf] = useState(false);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [resending, setResending] = useState(false);
  const [newActivationLink, setNewActivationLink] = useState<string | null>(null);
  const [infoCadastroEscolarTipo, setInfoCadastroEscolarTipo] = useState<'aluno' | 'responsavel' | 'geral' | null>(null);
  const [fichaAlunoId, setFichaAlunoId] = useState<string | null>(null);
  const [fichaResp, setFichaResp] = useState<any | null>(null);
  const [escolaFichaId, setEscolaFichaId] = useState<string | undefined>(undefined);

  const { data: redesData } = useCrud<any>('admin/redes');
  const { data: estadosData } = useCrud<any>('admin/estados');
  const { data: municipiosData } = useCrud<any>('admin/municipios');
  const { data: escolasData } = useCrud<any>('escolas');

  useEffect(() => {
    const st = location.state as any;
    if (st?.filtro) {
      setFiltro(st.filtro);
    }
    if (st?.openInvite) {
      resetWizard();
      if (st.perfil) {
        setForm(prev => ({ ...prev, perfil: st.perfil }));
      }
      setModalOpen(true);
    }
  }, [location.state]);

  const resetWizard = () => {
    setStep(1);
    setEditing(null);
    setInviteResult(null);
    setCpfError(null);
    setCpfWarning(null);
    setCheckingCpf(false);
    setEmailWarning(null);
    setCheckingEmail(false);
    setNewActivationLink(null);
    setForm({
      nome: '',
      email: '',
      telefone: '',
      perfil: 'ESCOLA',
      cargo: 'DIRETOR',
      cpf: '',
      dataNascimento: '',
      redeId: '',
      estadoId: '',
      municipioId: '',
      escolaId: ''
    });
  };

  const handlePerfilChange = (newPerfil: string) => {
    setForm(f => {
      const updated = { ...f, perfil: newPerfil };
      if (newPerfil === 'ADMIN') {
        updated.redeId = '';
        updated.estadoId = '';
        updated.municipioId = '';
        updated.escolaId = '';
        updated.cargo = 'DIRETOR';
      } else if (newPerfil === 'REDE') {
        updated.estadoId = '';
        updated.municipioId = '';
        updated.escolaId = '';
        updated.cargo = 'DIRETOR';
      } else if (newPerfil === 'ESTADUAL' || newPerfil === 'ESTADO') {
        updated.municipioId = '';
        updated.escolaId = '';
        updated.cargo = 'DIRETOR';
      } else if (newPerfil === 'MUNICIPAL') {
        updated.escolaId = '';
        updated.cargo = 'DIRETOR';
      } else if (newPerfil !== 'ESCOLA') {
        updated.cargo = 'DIRETOR';
      }
      return updated;
    });
  };

  const openNew = (perfil?: string) => {
    resetWizard();
    if (perfil) setForm(f => ({ ...f, perfil }));
    setModalOpen(true);
  };
  const openEdit = (u: Usuario) => {
    setEditing(u); setStep(1); setInviteResult(null); setEditTab('dados');
    setCpfError(null);
    setCheckingCpf(false);
    setNewActivationLink(null);

    // Carrega detalhes completos (com preferencias) do backend em background
    if (u.id) {
      api.get(`/admin/usuarios/${u.id}`)
        .then(res => {
          const fullUser = res.data?.data || res.data;
          if (fullUser) {
            setEditing(prev => (prev?.id === u.id ? { ...prev, ...fullUser } : prev));
            const resp = (fullUser as any).responsavelVinculado;
            setForm(prev => ({
              ...prev,
              cpf: fullUser.cpf ? formatCPF(fullUser.cpf) : (resp?.cpf ? formatCPF(resp.cpf) : prev.cpf),
              dataNascimento: fullUser.dataNascimento
                ? String(fullUser.dataNascimento).slice(0, 10)
                : (resp?.dataNascimento ? String(resp.dataNascimento).slice(0, 10) : prev.dataNascimento),
              telefone: (fullUser as any).telefone
                ? formatPhone((fullUser as any).telefone)
                : (resp?.telefone ? formatPhone(resp.telefone) : prev.telefone),
            }));
          }
        })
        .catch(() => { });
    }

    // Auto-resolver redeId, estadoId, municipioId a partir do escolaId se estiverem ausentes no usuário
    const resolvedEscolaId = (u as any).escolaId || '';
    let resolvedMunicipioId = (u as any).municipioId || '';
    let resolvedEstadoId = (u as any).estadoId || '';
    let resolvedRedeId = (u as any).redeId || '';

    if (resolvedEscolaId && (!resolvedMunicipioId || !resolvedEstadoId || !resolvedRedeId)) {
      const escolaObj = (escolasData || []).find((e: any) => e.id === resolvedEscolaId);
      if (escolaObj) {
        resolvedMunicipioId = escolaObj.municipioId || '';
        const munObj = (municipiosData || []).find((m: any) => m.id === resolvedMunicipioId);
        if (munObj) {
          resolvedEstadoId = munObj.estadoId || '';
          const estObj = (estadosData || []).find((est: any) => est.id === resolvedEstadoId);
          if (estObj) {
            resolvedRedeId = estObj.redeId || '';
          }
        }
      }
    }

    const rawCpf = u.cpf || (u as any).responsavelVinculado?.cpf || '';
    const rawDataNasc = u.dataNascimento || (u as any).responsavelVinculado?.dataNascimento || '';
    const rawTelefone = (u as any).telefone || (u as any).responsavelVinculado?.telefone || '';

    setForm({
      nome: u.nome,
      email: u.email,
      telefone: rawTelefone ? formatPhone(rawTelefone) : '',
      perfil: u.perfil,
      cargo: (u as any).cargo || 'DIRETOR',
      cpf: rawCpf ? formatCPF(rawCpf) : '',
      dataNascimento: rawDataNasc ? String(rawDataNasc).slice(0, 10) : '',
      redeId: resolvedRedeId,
      estadoId: resolvedEstadoId,
      municipioId: resolvedMunicipioId,
      escolaId: resolvedEscolaId,
    });
    setModalOpen(true);
  };

  const handleCpfBlur = async () => {
    const isResp = form.perfil?.toUpperCase() === 'RESPONSAVEL';
    if (!form.cpf) {
      if (isResp) {
        setCpfError('CPF é obrigatório para Responsável (11 dígitos).');
      } else {
        setCpfError(null);
      }
      setCpfWarning(null);
      return;
    }
    const cleanCpf = form.cpf.replace(/\D/g, '');
    if (cleanCpf.length === 0) {
      if (isResp) {
        setCpfError('CPF é obrigatório para Responsável (11 dígitos).');
      } else {
        setCpfError(null);
      }
      setCpfWarning(null);
      return;
    }
    if (cleanCpf.length !== 11) {
      setCpfError('CPF deve ter 11 dígitos.');
      setCpfWarning(null);
      return;
    }
    setCpfError(null);
    setCheckingCpf(true);
    try {
      const res = await api.get(`/validacoes/cpf/${cleanCpf}`);
      if (res.data?.exists) {
        const perfilStr = res.data.perfil ? ` (${res.data.perfil})` : res.data.tipo ? ` (${res.data.tipo})` : '';
        const escolaStr = res.data.escola ? ` — ${res.data.escola}` : '';
        setCpfWarning(`Atenção: CPF já cadastrado para ${res.data.nome}${perfilStr}${escolaStr}.`);
      } else {
        setCpfWarning(null);
      }
    } catch (err) {
      console.error('Erro ao verificar CPF:', err);
      setCpfError('Falha ao verificar CPF.');
    } finally {
      setCheckingCpf(false);
    }
  };

  const handleEmailBlur = async () => {
    if (!form.email) {
      setEmailWarning(null);
      return;
    }
    const cleanEmail = form.email.trim().toLowerCase();
    const isValidFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail);
    if (!isValidFormat) {
      setEmailWarning(null);
      return;
    }
    if (editing && editing.email?.toLowerCase() === cleanEmail) {
      setEmailWarning(null);
      return;
    }
    setCheckingEmail(true);
    try {
      const res = await api.get(`/validacoes/email/${encodeURIComponent(cleanEmail)}`);
      if (res.data?.exists) {
        const perfilStr = res.data.perfil ? ` (${res.data.perfil})` : '';
        const escolaStr = res.data.escola ? ` — ${res.data.escola}` : '';
        setEmailWarning(`Atenção: E-mail já cadastrado para ${res.data.nome}${perfilStr}${escolaStr}.`);
      } else {
        setEmailWarning(null);
      }
    } catch (err) {
      console.error('Erro ao verificar email:', err);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleResend = async (userId: string) => {
    setResending(true);
    try {
      const { data } = await api.post(`${ROUTES.admin.usuarios}/${userId}/resend`);
      if (data.activationLink) {
        setNewActivationLink(data.activationLink);
      }
      void refetch();
      if (editing && data.user) {
        setEditing({
          ...editing,
          tokenAtivacao: data.user.tokenAtivacao,
          tokenExpiraEm: data.user.tokenExpiraEm,
          statusConta: 'PENDENTE'
        } as any);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao gerar novo link');
    } finally {
      setResending(false);
    }
  };

  const canProceedStep1 = () => {
    const isNomeValid = form.nome.trim().length > 2;
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    const hasEmailConflict = !!emailWarning;
    const hasCpfConflict = !!cpfWarning || !!cpfError;
    const isResponsavel = form.perfil?.toUpperCase() === 'RESPONSAVEL';
    const isAluno = form.perfil?.toUpperCase() === 'ALUNO';
    const cleanCpf = form.cpf.replace(/\D/g, '');
    const isCpfValid = isResponsavel
      ? cleanCpf.length === 11 && !hasCpfConflict
      : (!form.cpf || (cleanCpf.length === 11 && !hasCpfConflict));
    const isBirthdateValid = (isAluno || isResponsavel) ? !!form.dataNascimento : true;

    return isNomeValid && isEmailValid && !hasEmailConflict && isCpfValid && isBirthdateValid;
  };

  const steps = editing
    ? [{ n: 1, label: 'Dados' }]
    : [{ n: 1, label: 'Identificação' }, { n: 2, label: 'Perfil' }];

  const handleSave = async () => {
    if (!form.nome.trim() || !form.email.trim()) { toast.warning('Nome e Email são obrigatórios'); return; }
    if (cpfError) { toast.warning(cpfError); return; }

    const perfilUpper = form.perfil?.toUpperCase();
    const isResponsavel = perfilUpper === 'RESPONSAVEL';
    const isAluno = perfilUpper === 'ALUNO';
    const cleanCpf = form.cpf.replace(/\D/g, '');

    if (isResponsavel) {
      if (!cleanCpf || cleanCpf.length !== 11) {
        toast.warning('CPF válido com 11 dígitos é obrigatório para Responsável');
        return;
      }
      if (!form.dataNascimento) {
        toast.warning('Data de nascimento é obrigatória para Responsável');
        return;
      }
    } else {
      if (cleanCpf && cleanCpf.length !== 11) { toast.warning('CPF informado deve ter 11 dígitos'); return; }
      if (isAluno && !form.dataNascimento) { toast.warning('Data de nascimento é obrigatória para Aluno'); return; }
    }

    // 1. Validação de Cargo para ESCOLA
    if (perfilUpper === 'ESCOLA' && !form.cargo) {
      toast.warning('Selecione o Cargo na Escola');
      return;
    }

    // 2. Validação da Cadeia de Hierarquia Obrigatória
    if (perfilUpper !== 'ADMIN') {
      if (!form.redeId) {
        toast.warning('Selecione a Rede');
        return;
      }
    }

    if (!['ADMIN', 'REDE'].includes(perfilUpper)) {
      if (!form.estadoId) {
        toast.warning('Selecione o Estado');
        return;
      }
    }

    if (!['ADMIN', 'REDE', 'ESTADUAL', 'ESTADO'].includes(perfilUpper)) {
      if (!form.municipioId) {
        toast.warning('Selecione o Município');
        return;
      }
    }

    if (['ESCOLA', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'].includes(perfilUpper)) {
      if (!form.escolaId) {
        toast.warning('Selecione a Escola');
        return;
      }
    }
    setSaving(true);
    try {
      const isSchoolScoped = ['ESCOLA', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'].includes(form.perfil?.toUpperCase());
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          nome: form.nome,
          email: form.email,
          perfil: form.perfil,
          cpf: form.cpf || null,
          dataNascimento: form.dataNascimento ? new Date(form.dataNascimento).toISOString() : null,
          redeId: form.perfil === 'REDE' ? (form.redeId || null) : null,
          estadoId: form.perfil === 'ESTADUAL' || form.perfil === 'ESTADO' ? (form.estadoId || null) : null,
          municipioId: form.perfil === 'MUNICIPAL' ? (form.municipioId || null) : null,
          escolaId: isSchoolScoped ? (form.escolaId || null) : null,
          cargo: form.perfil === 'ESCOLA' ? form.cargo : null
        } as any);
        toast.success('Usuário atualizado!');
        setModalOpen(false); resetWizard();
      } else {
        const payload: any = {
          nome: form.nome,
          email: form.email,
          perfil: form.perfil,
          cpf: form.cpf || null,
          dataNascimento: form.dataNascimento ? new Date(form.dataNascimento).toISOString() : null,
          redeId: form.perfil === 'REDE' ? (form.redeId || null) : null,
          estadoId: form.perfil === 'ESTADUAL' || form.perfil === 'ESTADO' ? (form.estadoId || null) : null,
          municipioId: form.perfil === 'MUNICIPAL' ? (form.municipioId || null) : null,
          escolaId: isSchoolScoped ? (form.escolaId || null) : null,
        };
        if (form.perfil === 'ESCOLA') payload.cargo = form.cargo;
        const res = await api.post(ROUTES.admin.usuarios, payload);
        setInviteResult({
          activationLink: res.data.activationLink,
          whatsappLink: res.data.whatsappLink,
          emailSent: res.data.emailSent,
          userName: form.nome,
        });
        setStep(steps.length + 1);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error?.message || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleCloseInvite = () => { setInviteResult(null); setModalOpen(false); resetWizard(); };

  const handleHardDeleteUser = (u: Usuario) => {
    setUserToHardDelete(u);
  };

  const confirmHardDeleteUser = async () => {
    if (!userToHardDelete) return;
    const u = userToHardDelete;
    setUserToHardDelete(null);
    try {
      await api.delete(`/admin/usuarios/${u.id}`);
      toast.success('Usuário excluído definitivamente!');
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao excluir usuário');
    }
  };

  const requestDelete = (u: Usuario) => setDeleteConfirm(u);
  const confirmDeleteUser = async () => {
    if (!deleteConfirm) return;
    const id = deleteConfirm.id;
    setDeleteConfirm(null);
    try {
      const { data: res } = await api.patch(`/admin/usuarios/${id}/toggle`);
      if (res?.statusConta === 'PENDENTE' || res?.reconvidado || !res?.ativadoEm) {
        toast.success('Usuário reativado! Novo link de ativação gerado.');
      } else {
        toast.success('Status do usuário atualizado!');
      }
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao alterar status do usuário');
    }
  };

  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR') : 'Nunca';

  const isDemoUser = (u: Usuario) => {
    const email = (u.email || '').toLowerCase();
    const nome = (u.nome || '').toLowerCase();
    const escola = (u as any).escola;
    return (
      (u as any).isDemo === true ||
      escola?.isDemo === true ||
      email.includes('demo') ||
      email.includes('.mock') ||
      nome.includes('demo') ||
      nome.startsWith('pai demonstração') ||
      nome.startsWith('responsavel legado') ||
      nome.startsWith('responsável audit') ||
      nome.startsWith('aluno audit') ||
      nome.startsWith('pai/mãe aluno')
    );
  };

  const totalDemoUsuarios = useMemo(() => {
    return (data || []).filter(isDemoUser).length;
  }, [data]);

  // Contagem de usuários demo especificamente para a aba/filtro ativo
  const tabDemoUsuarios = useMemo(() => {
    const all = data || [];
    let tabData = all;
    if (['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA'].includes(filtro)) tabData = all.filter((u: Usuario) => matchesCargoFiltro(u, filtro));
    else if (filtro !== 'TODOS') tabData = all.filter((u: Usuario) => u.perfil === filtro);

    return tabData.filter(isDemoUser).length;
  }, [data, filtro]);

  // Conjunto base respeitando o toggle "Exibir Demo"
  const baseUsers = useMemo(() => {
    const all = data || [];
    if (!exibirDemo) {
      return all.filter(u => !isDemoUser(u));
    }
    return all;
  }, [data, exibirDemo]);

  // Classificação dos 5 status operacionais de conta e cadastro
  const getUserStatusCategory = (u: Usuario): 'ATIVO' | 'AGUARDANDO' | 'EXPIRADO' | 'CADASTRO' | 'DESATIVADO' => {
    const uAny = u as any;
    const isCadastro = Boolean(uAny.isCadastroEscolar || (u.statusConta as string) === 'CADASTRO_ESCOLAR');
    if (isCadastro) return 'CADASTRO';

    const statusConta = (u.statusConta || '').toUpperCase();
    const isDesativado = u.ativo === false || statusConta === 'SUSPENSA' || statusConta === 'BLOQUEADA';
    if (isDesativado) return 'DESATIVADO';

    const isAtivo = statusConta === 'ATIVA' || (u.ativo && statusConta !== 'PENDENTE');
    if (isAtivo) return 'ATIVO';

    const isExpirado = Boolean(u.tokenExpiraEm && new Date(u.tokenExpiraEm).getTime() <= Date.now());
    if (isExpirado) return 'EXPIRADO';

    return 'AGUARDANDO';
  };

  // Conjunto de usuários da aba/perfil selecionado
  const tabUsers = useMemo(() => {
    let list = baseUsers;
    if (['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA'].includes(filtro)) list = list.filter((u: Usuario) => matchesCargoFiltro(u, filtro));
    else if (filtro !== 'TODOS') list = list.filter((u: Usuario) => u.perfil === filtro);
    return list;
  }, [baseUsers, filtro]);

  // Contagens dos 5 status operacionais (sensíveis à aba ativa)
  const statusCounts = useMemo(() => {
    const counts = {
      ATIVO: 0,
      AGUARDANDO: 0,
      EXPIRADO: 0,
      CADASTRO: 0,
      DESATIVADO: 0,
      TOTAL: tabUsers.length,
    };
    tabUsers.forEach((u) => {
      const cat = getUserStatusCategory(u);
      counts[cat]++;
    });
    return counts;
  }, [tabUsers]);

  // KPIs por perfil
  const stats = useMemo(() => {
    const all = baseUsers;
    const byPerfil: Record<string, number> = {};
    all.forEach((u: Usuario) => { byPerfil[u.perfil] = (byPerfil[u.perfil] || 0) + 1; });
    const ativos = all.filter((u: Usuario) => u.ativo).length;
    return { total: all.length, ativos, byPerfil };
  }, [baseUsers]);

  // Dados filtrados — suporta filtro de aba de perfil, filtro de status card e busca textual
  const filteredData = useMemo(() => {
    let list = tabUsers;
    if (statusFiltro !== 'TODOS') {
      list = list.filter((u) => getUserStatusCategory(u) === statusFiltro);
    }

    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(u =>
      (u.nome || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      ((u as any).cpf && (u as any).cpf.toLowerCase().includes(q)) ||
      ((u as any).matricula && (u as any).matricula.toLowerCase().includes(q)) ||
      (u.escola?.nome && u.escola.nome.toLowerCase().includes(q)) ||
      (u.telefone && u.telefone.toLowerCase().includes(q))
    );
  }, [tabUsers, statusFiltro, searchQuery]);

  // Contagem por filtro (incluindo cargos como filtros virtuais) baseada em baseUsers
  const filterCounts = useMemo(() => {
    const all = baseUsers;
    const counts: Record<string, number> = {};
    for (const key of FILTRO_ORDER) {
      if (['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA'].includes(key)) counts[key] = all.filter((u: Usuario) => matchesCargoFiltro(u, key)).length;
      else counts[key] = all.filter((u: Usuario) => u.perfil === key).length;
    }
    return counts;
  }, [baseUsers]);

  // Top 4 KPIs para o hero
  const topPerfis = useMemo(() => {
    return Object.entries(stats.byPerfil)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([perfil, count]) => ({
        perfil, count,
        ...(PERFIL_CONFIG[perfil] || { label: perfil, color: '#6b7280', icon: Users }),
      }));
  }, [stats]);

  const columns: ColumnDef<Usuario>[] = [
    {
      accessorKey: 'nome', header: 'Nome', cell: ({ row }) => {
        const u = row.original as any;
        const cfg = PERFIL_CONFIG[u.perfil];
        const Icon = cfg?.icon || Shield;
        const email = u.email;
        const isDemo = isDemoUser(row.original);
        const isCadastro = u.isCadastroEscolar || u.statusConta === 'CADASTRO_ESCOLAR';

        let subInfo: string | null = null;
        if (isCadastro) {
          if (u.perfil === 'ALUNO') {
            subInfo = [u.escola?.nome, u.turmaNome ? `Turma: ${u.turmaNome}` : null, u.matricula ? `Matrícula: ${u.matricula}` : null].filter(Boolean).join(' • ');
          } else if (u.perfil === 'RESPONSAVEL') {
            subInfo = [u.escola?.nome, u.alunosVinculados?.length ? `Aluno(s): ${u.alunosVinculados.join(', ')}` : null].filter(Boolean).join(' • ');
          }
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <UserAvatar src={u.fotoUrl} name={u.nome} role={u.perfil} size={32} />
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>{u.nome}</span>
                {isDemo && (
                  <span style={{
                    padding: '1px 5px',
                    borderRadius: 4,
                    fontSize: '.62rem',
                    fontWeight: 700,
                    background: 'rgba(59, 130, 246, 0.15)',
                    color: '#2563eb',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}>
                    Demo
                  </span>
                )}
              </div>
              {subInfo && (
                <div style={{ fontSize: '.72rem', color: '#64748b', marginTop: 1 }}>
                  {subInfo}
                </div>
              )}
              {email && (
                <a href={`mailto:${email}`} style={{ fontSize: '.7rem', color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}
                  onClick={(e) => e.stopPropagation()} title={`Enviar email para ${email}`}>
                  <Mail size={10} />{email}
                </a>
              )}
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'perfil', header: 'Perfil', cell: ({ row }) => {
        const cfg = PERFIL_CONFIG[row.original.perfil];
        const color = cfg?.color || '#6b7280';
        return <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: `${color}20`, color }}>{cfg?.label || row.original.perfil}</span>;
      }
    },
    {
      accessorKey: 'cpf', header: 'Documento', cell: ({ row }) => {
        const cpf = row.original.cpf;
        const rg = (row.original as any).rg;
        const status = row.original.documentoStatus;

        if (!cpf && !rg) {
          return (
            <span style={{ fontSize: '.76rem', color: 'var(--text-muted, #9ca3af)', fontWeight: 500 }}>
              Sem Documento
            </span>
          );
        }

        const formattedDoc = cpf ? formatCPF(cpf) : `RG: ${rg}`;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-main, #334155)', fontFamily: 'monospace' }}>
              {formattedDoc}
            </span>
            {status && status !== 'SEM_DOCUMENTO' && (
              <span style={{
                fontSize: '.62rem',
                fontWeight: 700,
                padding: '1px 5px',
                borderRadius: 4,
                width: 'fit-content',
                background: status === 'APROVADO' ? '#10b98118' : (status === 'PENDENTE' ? '#f59e0b18' : '#ef444418'),
                color: status === 'APROVADO' ? '#10b981' : (status === 'PENDENTE' ? '#f59e0b' : '#ef4444'),
              }}>
                {status === 'APROVADO' ? 'Aprovado' : (status === 'PENDENTE' ? 'Doc Pendente' : 'Doc Rejeitado')}
              </span>
            )}
          </div>
        );
      }
    },
    { accessorKey: 'ultimoLogin', header: 'Último Login', cell: ({ row }) => formatDate(row.original.ultimoLogin) },
    {
      accessorKey: 'ativo', header: 'Status', cell: ({ row }) => {
        const u = row.original as any;
        const isCadastro = u.isCadastroEscolar || u.statusConta === 'CADASTRO_ESCOLAR';

        if (isCadastro) {
          return (
            <span
              onClick={(e) => {
                e.stopPropagation();
                setInfoCadastroEscolarTipo(u.perfil === 'ALUNO' ? 'aluno' : 'responsavel');
              }}
              style={{
                padding: '2px 8px',
                borderRadius: 6,
                fontSize: '.68rem',
                fontWeight: 700,
                background: 'rgba(100, 116, 139, 0.12)',
                color: '#475569',
                border: '1px solid rgba(100, 116, 139, 0.25)',
                textTransform: 'uppercase',
                letterSpacing: '.03em',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4
              }}
              title="Clique para entender o status de Cadastro Escolar"
            >
              Cadastro Escolar
            </span>
          );
        }

        const statusConta = (u.statusConta || '').toUpperCase();
        const isDesativado = u.ativo === false || statusConta === 'SUSPENSA' || statusConta === 'BLOQUEADA';
        const isAtivo = !isDesativado && (statusConta === 'ATIVA' || (u.ativo && statusConta !== 'PENDENTE'));
        const isExpirado = !isDesativado && !isAtivo && Boolean(u.tokenExpiraEm && new Date(u.tokenExpiraEm).getTime() <= Date.now());

        if (isDesativado) {
          return (
            <span style={{
              padding: '2px 8px', borderRadius: 6, fontSize: '.68rem', fontWeight: 700,
              background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.25)',
              textTransform: 'uppercase', letterSpacing: '.03em',
            }}>
              Desativado
            </span>
          );
        }
        if (isAtivo) {
          return (
            <span style={{
              padding: '2px 8px', borderRadius: 6, fontSize: '.68rem', fontWeight: 700,
              background: 'rgba(16, 185, 129, 0.12)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.25)',
              textTransform: 'uppercase', letterSpacing: '.03em',
            }}>
              Ativo
            </span>
          );
        }
        if (isExpirado) {
          return (
            <span style={{
              padding: '2px 8px', borderRadius: 6, fontSize: '.68rem', fontWeight: 700,
              background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.25)',
              textTransform: 'uppercase', letterSpacing: '.03em',
            }}>
              Link Expirado
            </span>
          );
        }
        return (
          <span style={{
            padding: '2px 8px', borderRadius: 6, fontSize: '.68rem', fontWeight: 700,
            background: 'rgba(245, 158, 11, 0.12)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.25)',
            textTransform: 'uppercase', letterSpacing: '.03em',
          }}>
            Aguardando
          </span>
        );
      }
    },
    {
      id: 'actions', header: '', cell: ({ row }) => {
        const u = row.original as any;
        const isCadastro = u.isCadastroEscolar || u.statusConta === 'CADASTRO_ESCOLAR';

        if (isCadastro) {
          if (u.perfil === 'ALUNO') {
            const alunoTargetId = u.alunoId || String(u.id).replace('aluno-', '');
            return (
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  className="action-btn"
                  title="Visualizar Ficha Completa do Aluno"
                  style={{ color: '#0891b2' }}
                  onClick={() => {
                    setFichaAlunoId(alunoTargetId);
                    setEscolaFichaId(u.escolaId);
                  }}
                >
                  <Eye size={15} />
                </button>
              </div>
            );
          }

          if (u.perfil === 'RESPONSAVEL') {
            const respTargetId = u.responsavelId || String(u.id).replace('resp-', '');
            return (
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  className="action-btn"
                  title="Visualizar Ficha do Responsável"
                  style={{ color: '#ec4899' }}
                  onClick={() => {
                    setFichaResp({ id: respTargetId, escolaId: u.escolaId });
                  }}
                >
                  <Eye size={15} />
                </button>
                <button
                  className="action-btn"
                  title="Cadastrar Usuário / Enviar Convite para Responsável"
                  style={{ color: '#6366f1' }}
                  onClick={() => {
                    resetWizard();
                    setForm({
                      nome: u.nome || '',
                      email: u.email || '',
                      telefone: u.telefone ? formatPhone(u.telefone) : '',
                      perfil: 'RESPONSAVEL',
                      cargo: 'RESPONSAVEL',
                      cpf: u.cpf ? formatCPF(u.cpf) : '',
                      dataNascimento: u.dataNascimento ? String(u.dataNascimento).slice(0, 10) : '',
                      redeId: '',
                      estadoId: '',
                      municipioId: '',
                      escolaId: u.escolaId || '',
                    });
                    setModalOpen(true);
                  }}
                >
                  <UserPlus size={15} />
                </button>
              </div>
            );
          }
        }

        const tel = (row.original as any).telefone;
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className="action-btn"
              title="Ver Detalhes do Usuário"
              style={{ color: '#8b5cf6' }}
              onClick={() => {
                setDetailUser(row.original);
                if (row.original.id) {
                  api.get(`/admin/usuarios/${row.original.id}`)
                    .then(res => {
                      const full = res.data?.data || res.data;
                      if (full) setDetailUser(prev => (prev?.id === full.id ? { ...prev, ...full } : prev));
                    })
                    .catch(() => {});
                }
              }}
            >
              <Eye size={15} />
            </button>
            <button
              className="action-btn"
              title={tel ? "Enviar WhatsApp" : "WhatsApp não disponível"}
              style={{ color: '#25d366', opacity: tel ? 1 : 0.4, cursor: tel ? 'pointer' : 'default' }}
              disabled={!tel}
              onClick={() => {
                if (tel) {
                  const cleanPhone = tel.replace(/\D/g, '');
                  const phoneIntl = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
                  window.open(`https://wa.me/${phoneIntl}?text=${encodeURIComponent(`Olá, ${row.original.nome}! Mensagem enviada via Aluno Conectado.`)}`, '_blank');
                }
              }}
            >
              <MessageCircle size={15} />
            </button>
            <button className="action-btn" onClick={() => openEdit(row.original)} title="Editar Usuário"><Pencil size={15} /></button>
            <button
              className="action-btn"
              title="Documentos de Identificação"
              style={{ color: '#3b82f6' }}
              onClick={() => setAuditUser(row.original)}
            >
              <FileText size={15} />
            </button>
            {devSettings.allowDeleteUsuarios && (
              row.original.ativo ? (
                <button className="action-btn action-btn-danger" onClick={() => requestDelete(row.original)} title="Desativar"><UserX size={15} /></button>
              ) : (
                <>
                  <button className="action-btn" style={{ color: '#10b981' }} onClick={() => requestDelete(row.original)} title="Ativar"><UserCheck size={15} /></button>
                  {devSettings.allowHardDelete && (
                    <button className="action-btn action-btn-danger" onClick={() => handleHardDeleteUser(row.original)} title="Excluir Definitivamente"><Trash2 size={15} /></button>
                  )}
                </>
              )
            )}
          </div>
        );
      }
    },
  ];

  const renderEmptyStateForUsers = () => {
    if (searchQuery.trim()) {
      return (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}>
          <Shield size={36} style={{ color: 'var(--text-muted, #9ca3af)', marginBottom: 12, opacity: 0.4, margin: '0 auto' }} />
          <div style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--text-main, #1e293b)' }}>Nenhum usuário encontrado</div>
          <div style={{ fontSize: '.78rem', marginTop: 4, color: 'var(--text-muted)' }}>Tente pesquisar por outros termos.</div>
        </div>
      );
    }

    if (filtro === 'ALUNO') {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px 50px' }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(8, 145, 178, 0.12)',
            color: '#0891b2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px'
          }}>
            <GraduationCap size={26} />
          </div>
          <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
            Nenhum aluno com conta de acesso ao portal
          </h3>
          <p style={{ fontSize: '.82rem', color: 'var(--text-muted, #6b7280)', maxWidth: 440, margin: '0 auto 16px', lineHeight: 1.5 }}>
            Os estudantes matriculados nas escolas não necessitam obrigatoriamente de conta de login web.
            Para visualizar a lista completa de estudantes, matrículas, notas e RFID, acesse o módulo de <strong>Alunos</strong>.
          </p>
          <button
            type="button"
            onClick={() => navigate(location.pathname.startsWith('/master') ? '/master/escolas' : '/admin/escolas')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 18px',
              borderRadius: 8,
              background: '#0891b2',
              color: 'white',
              fontSize: '.82rem',
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(8, 145, 178, 0.25)',
            }}
          >
            <span>Ir para Gestão de Escolas</span>
            <ExternalLink size={14} />
          </button>
        </div>
      );
    }

    if (filtro === 'RESPONSAVEL') {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px 50px' }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(236, 72, 153, 0.12)',
            color: '#ec4899',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px'
          }}>
            <UserCheck size={26} />
          </div>
          <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
            Nenhum responsável com conta de acesso ao portal
          </h3>
          <p style={{ fontSize: '.82rem', color: 'var(--text-muted, #6b7280)', maxWidth: 460, margin: '0 auto 16px', lineHeight: 1.5 }}>
            Os responsáveis cadastrados na secretaria escolar não necessitam obrigatoriamente de conta de login web ativa.
            Para visualizar a lista completa de pais e responsáveis vinculados aos estudantes, acesse a gestão escolar.
          </p>
          <button
            type="button"
            onClick={() => navigate(location.pathname.startsWith('/master') ? '/master/escolas' : '/admin/escolas')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 18px',
              borderRadius: 8,
              background: '#ec4899',
              color: 'white',
              fontSize: '.82rem',
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(236, 72, 153, 0.25)',
            }}
          >
            <span>Ir para Gestão de Escolas</span>
            <ExternalLink size={14} />
          </button>
        </div>
      );
    }

    const cfg = PERFIL_CONFIG[filtro] || { label: filtro, color: '#6b7280', icon: Users };
    const Icon = cfg.icon;

    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <Icon size={44} style={{ color: cfg.color, marginBottom: 16, opacity: 0.7, margin: '0 auto 12px' }} />
        <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
          Não há usuários com perfil {cfg.label}
        </h3>
        <p style={{ fontSize: '.82rem', color: 'var(--text-muted, #6b7280)', maxWidth: 360, margin: '0 auto', lineHeight: 1.45 }}>
          Não encontramos nenhum usuário ativo ou cadastrado com esse perfil no momento.
        </p>
      </div>
    );
  };

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 40%, #3730a3 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
        flexShrink: 0,
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(99,102,241,.12)' }} />
        <div style={{ position: 'absolute', bottom: -40, left: '30%', width: 160, height: 160, borderRadius: '50%', background: 'rgba(59,130,246,.08)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Users size={18} style={{ color: '#93c5fd' }} />
                <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>Gestão</span>
              </div>
              <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Usuários</h1>
              <p style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', marginTop: 4 }}>
                {stats.total} usuários cadastrados · {stats.ativos} ativos
              </p>
            </div>
            <button onClick={() => openNew()} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
              background: '#93c5fd', color: '#1e3a5f', fontWeight: 700, fontSize: '.85rem', border: 'none', cursor: 'pointer',
            }}>
              <UserPlus size={16} /> Convidar Usuário
            </button>
          </div>
        </div>
      </div>

      {/* ═══ TABELA CONTAINER COM HEADER ESCURO ═══ */}
      <div style={{ borderRadius: 12, border: '1px solid var(--border, #e5e7eb)', overflow: 'hidden', display: 'flex', flexDirection: 'column', marginBottom: 32 }}>
        {/* Tier 1: Abas de Perfis */}
        <div style={{
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          padding: '12px 16px 8px',
          background: '#111827',
          borderBottom: '1px solid rgba(255,255,255,.1)',
        }}>
          <button onClick={() => { setFiltro('TODOS'); setStatusFiltro('TODOS'); }} style={{
            height: 30,
            padding: '0 12px',
            borderRadius: 8,
            fontSize: '.74rem',
            fontWeight: 700,
            border: filtro === 'TODOS' ? '1px solid #818cf8' : '1px solid rgba(255,255,255,.15)',
            background: filtro === 'TODOS' ? 'rgba(129,140,248,.18)' : 'rgba(255,255,255,.04)',
            color: filtro === 'TODOS' ? '#a5b4fc' : 'rgba(255,255,255,.6)',
            cursor: 'pointer',
            transition: 'all .15s ease',
          }}>
            Todos ({stats.total})
          </button>
          {FILTRO_ORDER.map(p => {
            const cfg = PERFIL_CONFIG[p] || { label: p, color: '#6b7280' };
            const count = filterCounts[p] || 0;
            const active = filtro === p;
            const isEmpty = count === 0;
            return (
              <button key={p} onClick={() => { setFiltro(p); setStatusFiltro('TODOS'); }} style={{
                height: 30,
                padding: '0 12px',
                borderRadius: 8,
                fontSize: '.74rem',
                fontWeight: 700,
                border: active ? `1px solid ${cfg.color}` : '1px solid rgba(255,255,255,.15)',
                background: active ? `${cfg.color}25` : 'rgba(255,255,255,.04)',
                color: active ? cfg.color : isEmpty ? 'rgba(255,255,255,.3)' : 'rgba(255,255,255,.6)',
                cursor: isEmpty && !active ? 'default' : 'pointer',
                opacity: isEmpty && !active ? 0.45 : 1,
                transition: 'all .15s ease',
              }}>
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Tier 2: Busca e totalizador */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          background: '#0f172a',
          borderBottom: '1px solid rgba(255,255,255,.08)',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, color: 'rgba(255,255,255,.4)', pointerEvents: 'none' }} />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar usuário por nome, email ou tel..."
              style={{
                height: 34,
                width: 280,
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,.15)',
                padding: '0 10px 0 32px',
                fontSize: '.82rem',
                background: 'rgba(255,255,255,.08)',
                color: 'rgba(255,255,255,.9)',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Checkbox sutil Exibir Demos (apenas se houver registros demo na aba) */}
            {tabDemoUsuarios > 0 && (
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '.78rem',
                fontWeight: 500,
                color: 'rgba(255,255,255,.75)',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'color .15s ease',
              }}>
                <input
                  type="checkbox"
                  checked={exibirDemo}
                  onChange={e => setExibirDemo(e.target.checked)}
                  style={{ accentColor: '#6366f1', width: 14, height: 14, cursor: 'pointer' }}
                />
                <span>Exibir {tabDemoUsuarios} demo{tabDemoUsuarios !== 1 ? 's' : ''}</span>
              </label>
            )}
            <span style={{ fontSize: '.78rem', color: 'rgba(255,255,255,.6)' }}>{filteredData.length} registro(s)</span>
          </div>
        </div>

        {/* Tier 3: KPIs de Status Operacional da Aba Ativa (Fundo cinza escuro integrado com o header) */}
        <div style={{
          padding: '14px 16px',
          background: 'linear-gradient(180deg, #111827 0%, #1e293b 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}>
          {[
            {
              key: 'ATIVO',
              label: 'Ativos',
              count: statusCounts.ATIVO,
              sub: 'Acesso liberado',
              color: '#10b981',
              icon: CheckCircle2,
            },
            {
              key: 'AGUARDANDO',
              label: 'Aguardando',
              count: statusCounts.AGUARDANDO,
              sub: 'Convite pendente',
              color: '#f59e0b',
              icon: Clock,
            },
            {
              key: 'EXPIRADO',
              label: 'Link Expirado',
              count: statusCounts.EXPIRADO,
              sub: 'Requer reenvio',
              color: '#ef4444',
              icon: AlertTriangle,
            },
            {
              key: 'CADASTRO',
              label: 'Cadastro Escolar',
              count: statusCounts.CADASTRO,
              sub: 'Registro acadêmico',
              color: '#6366f1',
              icon: FileText,
            },
            {
              key: 'DESATIVADO',
              label: 'Desativados',
              count: statusCounts.DESATIVADO,
              sub: 'Acesso suspenso',
              color: '#64748b',
              icon: UserX,
            },
          ].map((item) => {
            const isSelected = statusFiltro === item.key;
            const isEmpty = item.count === 0;
            const Icon = item.icon;
            return (
              <div
                key={item.key}
                onClick={() => {
                  if (isEmpty && !isSelected) return;
                  setStatusFiltro(statusFiltro === item.key ? 'TODOS' : (item.key as any));
                }}
                style={{
                  background: isSelected ? `${item.color}25` : (isEmpty ? 'rgba(255, 255, 255, 0.7)' : '#ffffff'),
                  borderRadius: 12,
                  padding: '12px 14px',
                  border: isSelected
                    ? `2px solid ${item.color}`
                    : (isEmpty ? '1px dashed rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.95)'),
                  boxShadow: isSelected
                    ? `0 0 0 3px ${item.color}35, 0 8px 24px ${item.color}45, inset 0 0 16px ${item.color}20`
                    : (isEmpty ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.16)'),
                  cursor: isEmpty ? 'default' : 'pointer',
                  opacity: isEmpty ? 0.4 : 1,
                  filter: isEmpty ? 'grayscale(0.75)' : 'none',
                  transition: 'all .2s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  transform: isSelected ? 'translateY(-2px)' : 'none',
                  userSelect: 'none',
                }}
                title={isEmpty ? `${item.label}: Nenhum registro nesta aba` : (isSelected ? `Clique para desmarcar filtro de ${item.label}` : `Filtrar por ${item.label}`)}
              >
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 9,
                  background: isEmpty ? '#f1f5f9' : (isSelected ? item.color : `${item.color}18`),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all .2s ease',
                }}>
                  <Icon size={18} style={{ color: isEmpty ? '#94a3b8' : (isSelected ? '#ffffff' : item.color) }} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontSize: '.74rem',
                    color: isSelected ? '#ffffff' : '#475569',
                    fontWeight: isSelected ? 700 : 600,
                    letterSpacing: isSelected ? '0.01em' : 'normal',
                  }}>
                    {item.label}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{
                      fontSize: '1.22rem',
                      fontWeight: 800,
                      color: isSelected ? '#ffffff' : '#0f172a',
                      lineHeight: 1.2,
                      textShadow: isSelected ? `0 0 12px ${item.color}80` : 'none',
                    }}>
                      {item.count}
                    </span>
                    <span style={{
                      fontSize: '.68rem',
                      color: isSelected ? 'rgba(255, 255, 255, 0.88)' : '#64748b',
                      fontWeight: isSelected ? 600 : 500,
                    }}>
                      {item.sub}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabela de dados */}
        <div style={{ padding: '0 0 4px' }}>
          {filteredData.length === 0 ? (
            renderEmptyStateForUsers()
          ) : (
            <DataTable key={filtro} columns={columns} data={filteredData} isLoading={isLoading} searchPlaceholder="" showToolbar={false} />
          )}
        </div>

        {/* Banner Explicativo de Rodapé para a Aba ALUNO */}
        {filtro === 'ALUNO' && (
          <div style={{
            margin: '8px 14px 14px',
            padding: '14px 18px',
            borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)',
            border: '1px solid rgba(8, 145, 178, 0.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 280 }}>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: '#0891b2',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 2
              }}>
                <GraduationCap size={20} />
              </div>
              <div>
                <div style={{ fontSize: '.86rem', fontWeight: 700, color: 'var(--text-main, #0f172a)' }}>
                  Cadastro Escolar de Alunos (Acesso Institucional de Consulta)
                </div>
                <p style={{ margin: '3px 0 0', fontSize: '.78rem', color: 'var(--text-muted, #475569)', lineHeight: 1.5 }}>
                  Os registros de <strong>CADASTRO ESCOLAR</strong> são dados acadêmicos (matrícula, turma, chamadas, RFID e notas). 
                  <strong> No caso do aluno, não existe funcionalidade para usuário fazer login aqui — o cadastro fica somente como acesso de consulta institucional.</strong> O acompanhamento digital e avisos são realizados pelo aplicativo dos pais/responsáveis.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setInfoCadastroEscolarTipo('aluno')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '7px 13px',
                  borderRadius: 8,
                  background: 'white',
                  color: '#0891b2',
                  fontSize: '.76rem',
                  fontWeight: 700,
                  border: '1px solid rgba(8, 145, 178, 0.35)',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  whiteSpace: 'nowrap'
                }}
              >
                <HelpCircle size={13} />
                <span>O que é Cadastro Escolar?</span>
              </button>
              <button
                type="button"
                onClick={() => navigate(location.pathname.startsWith('/master') ? '/master/escolas' : '/admin/escolas')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '7px 13px',
                  borderRadius: 8,
                  background: '#0891b2',
                  color: 'white',
                  fontSize: '.76rem',
                  fontWeight: 700,
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(8, 145, 178, 0.2)',
                  whiteSpace: 'nowrap'
                }}
              >
                <span>Ir para Gestão de Escolas</span>
                <ExternalLink size={13} />
              </button>
            </div>
          </div>
        )}

        {/* Banner Explicativo de Rodapé para a Aba RESPONSAVEL */}
        {filtro === 'RESPONSAVEL' && (
          <div style={{
            margin: '8px 14px 14px',
            padding: '14px 18px',
            borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
            border: '1px solid rgba(236, 72, 153, 0.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 280 }}>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: '#ec4899',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 2
              }}>
                <UserCheck size={20} />
              </div>
              <div>
                <div style={{ fontSize: '.86rem', fontWeight: 700, color: 'var(--text-main, #0f172a)' }}>
                  Contas de Acesso vs. Cadastro Escolar de Responsáveis
                </div>
                <p style={{ margin: '3px 0 0', fontSize: '.78rem', color: 'var(--text-muted, #475569)', lineHeight: 1.5 }}>
                  Esta aba lista as <strong>contas de login/acesso ao App dos Pais</strong> e também os registros de <strong>CADASTRO ESCOLAR</strong> vinculados aos alunos nas escolas. Registros marcados como <strong>CADASTRO ESCOLAR</strong> ainda não possuem usuário de acesso criado — o login no aplicativo é ativado clicando no botão de Convidar.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setInfoCadastroEscolarTipo('responsavel')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '7px 13px',
                  borderRadius: 8,
                  background: 'white',
                  color: '#ec4899',
                  fontSize: '.76rem',
                  fontWeight: 700,
                  border: '1px solid rgba(236, 72, 153, 0.35)',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  whiteSpace: 'nowrap'
                }}
              >
                <HelpCircle size={13} />
                <span>O que é Cadastro Escolar?</span>
              </button>
              <button
                type="button"
                onClick={() => navigate(location.pathname.startsWith('/master') ? '/master/escolas' : '/admin/escolas')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '7px 13px',
                  borderRadius: 8,
                  background: '#ec4899',
                  color: 'white',
                  fontSize: '.76rem',
                  fontWeight: 700,
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(236, 72, 153, 0.2)',
                  whiteSpace: 'nowrap'
                }}
              >
                <span>Ir para Gestão de Escolas</span>
                <ExternalLink size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => { setModalOpen(false); resetWizard(); }}
        title=""
        size="lg"
        contentClassName="umu-wizard-modal"
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 10 }}>
            {inviteResult ? (
              <button className="btn-save" style={{ marginLeft: 'auto' }} onClick={handleCloseInvite}>Concluir</button>
            ) : editing && editTab !== 'dados' ? (
              <button className="btn-cancel" style={{ marginLeft: 'auto' }} onClick={() => { setModalOpen(false); resetWizard(); }}>
                Fechar
              </button>
            ) : (
              <>
                <button className="btn-cancel" onClick={() => step > 1 ? setStep(step - 1) : (() => { setModalOpen(false); resetWizard(); })()}>
                  {step > 1 ? '← Voltar' : 'Cancelar'}
                </button>
                {step < steps.length ? (
                  <button className="btn-save" onClick={() => {
                    if (step === 1) {
                      if (!form.nome.trim() || form.nome.trim().length < 3) {
                        toast.warning('Preencha o nome completo');
                        return;
                      }
                      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
                        toast.warning('Preencha um e-mail válido');
                        return;
                      }
                      if (emailWarning) {
                        toast.warning('E-mail informado já está em uso no sistema. Corrija para continuar.');
                        return;
                      }
                      if (cpfWarning) {
                        toast.warning('CPF informado já está cadastrado no sistema. Corrija para continuar.');
                        return;
                      }
                      if (cpfError) {
                        toast.warning(cpfError);
                        return;
                      }
                      if (!canProceedStep1()) {
                        toast.warning('Preencha os campos obrigatórios corretamente');
                        return;
                      }
                    }
                    setStep(step + 1);
                  }}>Próximo →</button>
                ) : (
                  <button className="btn-save" onClick={() => void handleSave()} disabled={saving}>
                    {saving ? 'Enviando convite...' : editing ? 'Salvar Alterações' : 'Enviar Convite'}
                  </button>
                )}
              </>
            )}
          </div>
        }
      >
        {/* Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 40%, #3730a3 100%)',
          borderRadius: '12px 12px 0 0', padding: '20px 24px 16px', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(99,102,241,.12)' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
              {editing ? 'Editar Usuário' : 'Convidar Usuário'}
            </h2>
            <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.78rem', margin: 0 }}>
              {editing ? 'Atualize os dados do usuário.' : 'Envie um convite para a plataforma Aluno Conectado.'}
            </p>
          </div>
          {/* Stepper */}
          {!editing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14 }}>
              {steps.map((s, i) => {
                const isCurrent = step === s.n;
                const isDone = step > s.n;
                return (
                  <div key={s.n} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '.7rem', fontWeight: 700,
                      background: isCurrent ? 'white' : isDone ? '#10b981' : 'rgba(255,255,255,.15)',
                      color: isCurrent ? '#1e40af' : isDone ? 'white' : 'rgba(255,255,255,.5)',
                    }}>
                      {isDone ? <Check size={14} /> : s.n}
                    </div>
                    <span style={{ color: isCurrent ? 'white' : 'rgba(255,255,255,.5)', fontSize: '.72rem', fontWeight: 600 }}>{s.label}</span>
                    {i < steps.length - 1 && <div style={{ width: 30, height: 2, background: isDone ? '#10b981' : 'rgba(255,255,255,.15)', borderRadius: 2 }} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto' }}>
          {/* Edit Tabs (only when editing) */}
          {editing && (
            <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border, #e5e7eb)', marginBottom: 16 }}>
              {[
                { key: 'dados' as const, label: 'Dados' },
                ...(['ESCOLA', 'PROFESSOR'].includes(form.perfil?.toUpperCase()) ? [{ key: 'permissoes' as const, label: 'Permissões' }] : []),
                { key: 'senha' as const, label: 'Senha' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setEditTab(tab.key)}
                  style={{
                    padding: '8px 16px', fontSize: '.8rem', fontWeight: 600,
                    border: 'none', cursor: 'pointer', transition: 'all .15s',
                    background: 'transparent',
                    color: editTab === tab.key ? '#6366f1' : '#6b7280',
                    borderBottom: editTab === tab.key ? '2px solid #6366f1' : '2px solid transparent',
                    marginBottom: '-2px',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Tab: Permissões */}
          {editing && editTab === 'permissoes' && (
            <UsuariosPermissoesTab
              user={editing}
              onUpdated={() => {
                refetch();
                if (editing?.id) {
                  api.get(`/admin/usuarios/${editing.id}`)
                    .then(res => {
                      const fullUser = res.data?.data || res.data;
                      if (fullUser) setEditing(fullUser);
                    })
                    .catch(() => { });
                }
              }}
            />
          )}

          {/* Tab: Senha */}
          {editing && editTab === 'senha' && (
            <UsuariosSenhaTab
              user={editing}
              onUpdated={refetch}
              onResend={handleResend}
              resending={resending}
              newActivationLink={newActivationLink}
            />
          )}

          {/* Step 1: Dados */}
          {step === 1 && (!editing || editTab === 'dados') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Row 1: Nome and Email */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group"><label className="form-label">Nome completo *</label>
                  <input className="form-input" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Maria da Silva" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email institucional *</label>
                  <input
                    className={`form-input${emailWarning ? ' border-amber-500 text-amber-900 bg-amber-50/50' : ''}`}
                    type="email"
                    value={form.email}
                    onChange={e => {
                      setForm({ ...form, email: e.target.value });
                      if (emailWarning) setEmailWarning(null);
                    }}
                    onBlur={handleEmailBlur}
                    placeholder="maria@escola.edu.br"
                    disabled={!!editing}
                  />
                  {checkingEmail && (
                    <span style={{ color: '#6366f1', fontSize: '0.72rem', marginTop: 4, display: 'block' }}>
                      Verificando disponibilidade do e-mail...
                    </span>
                  )}
                  {emailWarning && (
                    <div style={{
                      marginTop: 6,
                      padding: '6px 10px',
                      borderRadius: 6,
                      background: '#fef3c7',
                      border: '1px solid #fde68a',
                      color: '#92400e',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <AlertTriangle size={14} color="#d97706" style={{ flexShrink: 0 }} />
                      <span>{emailWarning}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2: CPF and Birthdate */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">
                    CPF {form.perfil?.toUpperCase() === 'RESPONSAVEL' ? '*' : '(Opcional)'}
                  </label>
                  <input
                    className={`form-input${cpfWarning ? ' border-amber-500 text-amber-900 bg-amber-50/50' : cpfError ? ' border-red-500 text-red-500' : ''}`}
                    value={form.cpf}
                    onChange={e => {
                      const formatted = formatCPF(e.target.value);
                      setForm({ ...form, cpf: formatted });
                      if (cpfError) setCpfError(null);
                      if (cpfWarning) setCpfWarning(null);
                    }}
                    onBlur={handleCpfBlur}
                    placeholder="000.000.000-00"
                  />
                  {cpfError && (
                    <span style={{ color: '#ef4444', fontSize: '0.72rem', marginTop: 4, display: 'block', fontWeight: 600 }}>
                      {cpfError}
                    </span>
                  )}
                  {cpfWarning && (
                    <div style={{
                      marginTop: 6,
                      padding: '6px 10px',
                      borderRadius: 6,
                      background: '#fef3c7',
                      border: '1px solid #fde68a',
                      color: '#92400e',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <AlertTriangle size={14} color="#d97706" style={{ flexShrink: 0 }} />
                      <span>{cpfWarning}</span>
                    </div>
                  )}
                  {checkingCpf && (
                    <span style={{ color: '#6366f1', fontSize: '0.72rem', marginTop: 4, display: 'block' }}>
                      Verificando disponibilidade do CPF...
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Data de Nascimento {['RESPONSAVEL', 'ALUNO'].includes(form.perfil?.toUpperCase()) ? '*' : '(Opcional)'}
                  </label>
                  <input className="form-input" type="date" value={form.dataNascimento} onChange={e => setForm({ ...form, dataNascimento: e.target.value })} />
                </div>
              </div>

              {/* Row 3: Telefone / WhatsApp */}
              <div className="form-group">
                <label className="form-label">
                  Telefone / WhatsApp (Celular) {['RESPONSAVEL', 'PROFESSOR', 'ESCOLA'].includes(form.perfil?.toUpperCase()) ? '*' : '(Opcional)'}
                </label>
                <input
                  className="form-input"
                  type="tel"
                  value={form.telefone || ''}
                  onChange={e => setForm({ ...form, telefone: formatPhone(e.target.value) })}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />
              </div>

              {!editing && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px',
                  borderRadius: 10, background: 'var(--bg-muted, #f0f4ff)', border: '1px solid var(--border-color, #e5e7eb)',
                }}>
                  <Mail size={16} style={{ color: '#1e40af', flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '.78rem', color: 'var(--text-main)' }}>Sem senha necessária</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
                      O usuário receberá um link de ativação para definir sua própria senha com segurança.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Perfil & Hierarquia */}
          {step === 2 && !editing && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Row 1: Perfil, Cargo na Escola */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {/* Perfil */}
                <div className="form-group">
                  <label className="form-label">Perfil *</label>
                  <select
                    className="form-select"
                    value={form.perfil}
                    onChange={e => handlePerfilChange(e.target.value)}
                  >
                    {PERFIS_CRIAVEIS.map(r => <option key={r} value={r}>{PERFIL_CONFIG[r]?.label || r}</option>)}
                  </select>
                </div>

                {/* Cargo na Escola */}
                <div className="form-group" style={{ opacity: form.perfil?.toUpperCase() !== 'ESCOLA' ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                  <label className="form-label">Cargo na Escola {form.perfil?.toUpperCase() === 'ESCOLA' && '*'}</label>
                  <select
                    className="form-select"
                    value={form.perfil?.toUpperCase() === 'ESCOLA' ? form.cargo : ''}
                    disabled={form.perfil?.toUpperCase() !== 'ESCOLA'}
                    onChange={e => setForm({ ...form, cargo: e.target.value })}
                  >
                    {form.perfil?.toUpperCase() !== 'ESCOLA' && <option value="">Não aplicável</option>}
                    {CARGOS_ESCOLA.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 2: Rede, Estado, Município, Escola */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
                {/* Rede */}
                {(() => {
                  const isBlocked = form.perfil?.toUpperCase() === 'ADMIN';
                  const isDisabled = isBlocked;
                  return (
                    <div className="form-group" style={{ opacity: isDisabled ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      <label className="form-label">Rede {!isBlocked && '*'}</label>
                      <select
                        className="form-select"
                        value={!isBlocked ? (form.redeId || '') : ''}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({
                            ...form,
                            redeId: val,
                            estadoId: '',
                            municipioId: '',
                            escolaId: ''
                          });
                        }}
                      >
                        {isBlocked ? (
                          <option value="">Não aplicável</option>
                        ) : (
                          <>
                            <option value="">Selecione</option>
                            {(redesData || [])
                              .filter((r: any) => r.ativo === true || r.id === form.redeId)
                              .map((r: any) => (
                                <option key={r.id} value={r.id}>{r.nome}</option>
                              ))}
                          </>
                        )}
                      </select>
                    </div>
                  );
                })()}

                {/* Estado */}
                {(() => {
                  const isBlocked = form.perfil?.toUpperCase() === 'ADMIN' || form.perfil?.toUpperCase() === 'REDE';
                  const isDisabled = isBlocked || !form.redeId;
                  return (
                    <div className="form-group" style={{ opacity: isDisabled ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      <label className="form-label">Estado {!isBlocked && '*'}</label>
                      <select
                        className="form-select"
                        value={!isBlocked ? (form.estadoId || '') : ''}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({
                            ...form,
                            estadoId: val,
                            municipioId: '',
                            escolaId: ''
                          });
                        }}
                      >
                        {isBlocked ? (
                          <option value="">Não aplicável</option>
                        ) : (
                          <>
                            <option value="">Selecione</option>
                            {(estadosData || [])
                              .filter((est: any) => est.redeId === form.redeId)
                              .map((est: any) => (
                                <option key={est.id} value={est.id}>{est.nome} ({est.sigla})</option>
                              ))}
                          </>
                        )}
                      </select>
                    </div>
                  );
                })()}

                {/* Município */}
                {(() => {
                  const isBlocked = ['ADMIN', 'REDE', 'ESTADUAL', 'ESTADO'].includes(form.perfil?.toUpperCase());
                  const isDisabled = isBlocked || !form.estadoId;
                  return (
                    <div className="form-group" style={{ opacity: isDisabled ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      <label className="form-label">Município {!isBlocked && '*'}</label>
                      <select
                        className="form-select"
                        value={!isBlocked ? (form.municipioId || '') : ''}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({
                            ...form,
                            municipioId: val,
                            escolaId: ''
                          });
                        }}
                      >
                        {isBlocked ? (
                          <option value="">Não aplicável</option>
                        ) : (
                          <>
                            <option value="">Selecione</option>
                            {(municipiosData || [])
                              .filter((m: any) => m.estadoId === form.estadoId)
                              .map((m: any) => (
                                <option key={m.id} value={m.id}>{m.nome}</option>
                              ))}
                          </>
                        )}
                      </select>
                    </div>
                  );
                })()}

                {/* Escola */}
                {(() => {
                  const isBlocked = !['ESCOLA', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'].includes(form.perfil?.toUpperCase());
                  const isDisabled = isBlocked || !form.municipioId;
                  return (
                    <div className="form-group" style={{ opacity: isDisabled ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      <label className="form-label">Escola {!isBlocked && '*'}</label>
                      <select
                        className="form-select"
                        value={!isBlocked ? (form.escolaId || '') : ''}
                        disabled={isDisabled}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            escolaId: e.target.value
                          });
                        }}
                      >
                        {isBlocked ? (
                          <option value="">Não aplicável</option>
                        ) : (
                          <>
                            <option value="">Selecione</option>
                            {(escolasData || [])
                              .filter((esc: any) => esc.municipioId === form.municipioId)
                              .map((esc: any) => (
                                <option key={esc.id} value={esc.id}>{esc.nome}</option>
                              ))}
                          </>
                        )}
                      </select>
                    </div>
                  );
                })()}
              </div>

              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px',
                borderRadius: 10, background: 'var(--bg-muted, #f0f4ff)', border: '1px solid var(--border-color, #e5e7eb)',
              }}>
                <Shield size={16} style={{ color: '#6366f1', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '.78rem', color: 'var(--text-main)' }}>Acesso por Perfil</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
                    O perfil define qual dashboard e quais funcionalidades o usuário terá acesso na plataforma.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Perfil select for editing */}
          {step === 1 && editing && editTab === 'dados' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Row 1: Perfil, Cargo na Escola */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
                {/* Perfil */}
                <div className="form-group">
                  <label className="form-label">Perfil</label>
                  <select
                    className="form-select"
                    value={form.perfil}
                    onChange={e => handlePerfilChange(e.target.value)}
                  >
                    {VALIDOS_PERFIS.map(r => <option key={r} value={r}>{PERFIL_CONFIG[r]?.label || r}</option>)}
                  </select>
                </div>

                {/* Cargo na Escola */}
                <div className="form-group" style={{ opacity: form.perfil?.toUpperCase() !== 'ESCOLA' ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                  <label className="form-label">Cargo na Escola {form.perfil?.toUpperCase() === 'ESCOLA' && '*'}</label>
                  <select
                    className="form-select"
                    value={form.perfil?.toUpperCase() === 'ESCOLA' ? form.cargo : ''}
                    disabled={form.perfil?.toUpperCase() !== 'ESCOLA'}
                    onChange={e => setForm({ ...form, cargo: e.target.value })}
                  >
                    {form.perfil?.toUpperCase() !== 'ESCOLA' && <option value="">Não aplicável</option>}
                    {CARGOS_ESCOLA.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 2: Rede, Estado, Município, Escola */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
                {/* Rede */}
                {(() => {
                  const isBlocked = form.perfil?.toUpperCase() === 'ADMIN';
                  const isDisabled = isBlocked;
                  return (
                    <div className="form-group" style={{ opacity: isDisabled ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      <label className="form-label">Rede {!isBlocked && '*'}</label>
                      <select
                        className="form-select"
                        value={!isBlocked ? (form.redeId || '') : ''}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({
                            ...form,
                            redeId: val,
                            estadoId: '',
                            municipioId: '',
                            escolaId: ''
                          });
                        }}
                      >
                        {isBlocked ? (
                          <option value="">Não aplicável</option>
                        ) : (
                          <>
                            <option value="">Selecione</option>
                            {(redesData || [])
                              .filter((r: any) => r.ativo === true || r.id === form.redeId)
                              .map((r: any) => (
                                <option key={r.id} value={r.id}>{r.nome}</option>
                              ))}
                          </>
                        )}
                      </select>
                    </div>
                  );
                })()}

                {/* Estado */}
                {(() => {
                  const isBlocked = form.perfil?.toUpperCase() === 'ADMIN' || form.perfil?.toUpperCase() === 'REDE';
                  const isDisabled = isBlocked || !form.redeId;
                  return (
                    <div className="form-group" style={{ opacity: isDisabled ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      <label className="form-label">Estado {!isBlocked && '*'}</label>
                      <select
                        className="form-select"
                        value={!isBlocked ? (form.estadoId || '') : ''}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({
                            ...form,
                            estadoId: val,
                            municipioId: '',
                            escolaId: ''
                          });
                        }}
                      >
                        {isBlocked ? (
                          <option value="">Não aplicável</option>
                        ) : (
                          <>
                            <option value="">Selecione</option>
                            {(estadosData || [])
                              .filter((est: any) => est.redeId === form.redeId)
                              .map((est: any) => (
                                <option key={est.id} value={est.id}>{est.nome} ({est.sigla})</option>
                              ))}
                          </>
                        )}
                      </select>
                    </div>
                  );
                })()}

                {/* Município */}
                {(() => {
                  const isBlocked = ['ADMIN', 'REDE', 'ESTADUAL', 'ESTADO'].includes(form.perfil?.toUpperCase());
                  const isDisabled = isBlocked || !form.estadoId;
                  return (
                    <div className="form-group" style={{ opacity: isDisabled ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      <label className="form-label">Município {!isBlocked && '*'}</label>
                      <select
                        className="form-select"
                        value={!isBlocked ? (form.municipioId || '') : ''}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({
                            ...form,
                            municipioId: val,
                            escolaId: ''
                          });
                        }}
                      >
                        {isBlocked ? (
                          <option value="">Não aplicável</option>
                        ) : (
                          <>
                            <option value="">Selecione</option>
                            {(municipiosData || [])
                              .filter((m: any) => m.estadoId === form.estadoId)
                              .map((m: any) => (
                                <option key={m.id} value={m.id}>{m.nome}</option>
                              ))}
                          </>
                        )}
                      </select>
                    </div>
                  );
                })()}

                {/* Escola */}
                {(() => {
                  const isBlocked = !['ESCOLA', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'].includes(form.perfil?.toUpperCase());
                  const isDisabled = isBlocked || !form.municipioId;
                  return (
                    <div className="form-group" style={{ opacity: isDisabled ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      <label className="form-label">Escola {!isBlocked && '*'}</label>
                      <select
                        className="form-select"
                        value={!isBlocked ? (form.escolaId || '') : ''}
                        disabled={isDisabled}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            escolaId: e.target.value
                          });
                        }}
                      >
                        {isBlocked ? (
                          <option value="">Não aplicável</option>
                        ) : (
                          <>
                            <option value="">Selecione</option>
                            {(escolasData || [])
                              .filter((esc: any) => esc.municipioId === form.municipioId)
                              .map((esc: any) => (
                                <option key={esc.id} value={esc.id}>{esc.nome}</option>
                              ))}
                          </>
                        )}
                      </select>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Success: InviteResultDialog */}
          {inviteResult && step > steps.length && (
            <InviteResultDialog
              open={true}
              nome={inviteResult.userName}
              email={form.email}
              perfil={form.perfil}
              activationLink={inviteResult.activationLink}
              whatsappLink={inviteResult.whatsappLink}
              emailSent={inviteResult.emailSent}
              onClose={handleCloseInvite}
            />
          )}
        </div>
      </Modal>

      {/* ═══ WHATSAPP DIALOG ═══ */}
      <Modal open={!!waDialog} onClose={() => setWaDialog(null)} title={`WhatsApp — ${waDialog?.nome || ''}`}
        footer={<>
          <button className="btn-cancel" onClick={() => setWaDialog(null)}>Cancelar</button>
          <button className="btn-save" style={{ background: '#25d366' }} onClick={() => {
            if (!waDialog) return;
            const tel = waDialog.telefone.replace(/\D/g, '');
            const url = `https://wa.me/55${tel}${waMsg ? `?text=${encodeURIComponent(waMsg)}` : ''}`;
            window.open(url, '_blank');
            setWaDialog(null);
          }}>Abrir WhatsApp</button>
        </>}>
        <div className="form-group">
          <label className="form-label">Telefone</label>
          <input className="form-input" value={waDialog?.telefone || ''} readOnly style={{ opacity: 0.7 }} />
        </div>
        <div className="form-group">
          <label className="form-label">Mensagem (opcional)</label>
          <textarea className="form-input" rows={4} value={waMsg} onChange={e => setWaMsg(e.target.value)}
            placeholder="Escreva uma mensagem para enviar junto..." style={{ resize: 'vertical' }} />
        </div>
      </Modal>

      <ConfirmModal
        open={!!deleteConfirm}
        title={deleteConfirm?.ativo ? "Desativar usuário" : "Ativar usuário"}
        message={deleteConfirm ? (
          deleteConfirm.ativo
            ? `Desativar "${deleteConfirm.nome}" (${deleteConfirm.email})? O acesso à plataforma será revogado.`
            : `Reativar "${deleteConfirm.nome}" (${deleteConfirm.email})? O acesso à plataforma será restabelecido.`
        ) : ''}
        confirmLabel={deleteConfirm?.ativo ? "Desativar" : "Ativar"}
        variant={deleteConfirm?.ativo ? 'danger' : 'success'}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDeleteUser()}
      />

      <Modal
        open={!!auditUser}
        onClose={() => setAuditUser(null)}
        title=""
        size="lg"
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 40%, #3730a3 100%)',
            borderRadius: '12px 12px 0 0', padding: '20px 24px 16px', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(99,102,241,.12)' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
                Documentos de Identificação
              </h2>
              <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.78rem', margin: 0 }}>
                Verifique ou envie os documentos de comprovação de identidade para {auditUser?.nome}.
              </p>
            </div>
          </div>

          <div style={{ padding: '20px 24px' }}>
            {/* Status and Info Alert */}
            {auditUser && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>Status da Verificação:</span>
                  {(() => {
                    const status = auditUser.documentoStatus || 'SEM_DOCUMENTO';
                    let badgeColor = '#6b7280';
                    let statusLabel = 'Sem Documento';
                    if (status === 'PENDENTE') { badgeColor = '#f59e0b'; statusLabel = 'Pendente'; }
                    else if (status === 'APROVADO') { badgeColor = '#10b981'; statusLabel = 'Aprovado'; }
                    else if (status === 'REJEITADO') { badgeColor = '#ef4444'; statusLabel = 'Rejeitado'; }
                    return (
                      <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: `${badgeColor}20`, color: badgeColor }}>
                        {statusLabel}
                      </span>
                    );
                  })()}
                </div>
                {auditUser.documentoStatus === 'REJEITADO' && auditUser.documentoMotivoRejeicao && (
                  <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.8rem', color: '#fca5a5' }}>
                    <strong>Motivo da rejeição:</strong> {auditUser.documentoMotivoRejeicao}
                  </div>
                )}
              </div>
            )}

            {/* Document Slots */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              {/* Frente */}
              <div style={{ border: '1px dashed var(--border-color, #e5e7eb)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--bg-muted, #f9fafb)' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: 10 }}>Frente do Documento</span>
                {docFrenteBase64 ? (
                  <div style={{ width: '100%', position: 'relative' }}>
                    <img
                      src={docFrenteBase64}
                      alt="Frente"
                      style={{ width: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)' }}
                    />
                    <button
                      type="button"
                      className="btn-cancel"
                      style={{ width: '100%', marginTop: 8, padding: '6px 12px', fontSize: '0.78rem' }}
                      onClick={() => {
                        const input = document.getElementById('frente-file-input');
                        if (input) input.click();
                      }}
                    >
                      Substituir Frente
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 180, width: '100%' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>Nenhuma imagem carregada</span>
                    <button
                      type="button"
                      className="btn-save"
                      style={{ padding: '6px 14px', fontSize: '0.78rem' }}
                      onClick={() => {
                        const input = document.getElementById('frente-file-input');
                        if (input) input.click();
                      }}
                    >
                      Selecionar Arquivo
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  id="frente-file-input"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setRedactorFile(file);
                      setRedactorTitle('Frente');
                    }
                    e.target.value = '';
                  }}
                />
              </div>

              {/* Verso */}
              <div style={{ border: '1px dashed var(--border-color, #e5e7eb)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--bg-muted, #f9fafb)' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: 10 }}>Verso do Documento</span>
                {docVersoBase64 ? (
                  <div style={{ width: '100%', position: 'relative' }}>
                    <img
                      src={docVersoBase64}
                      alt="Verso"
                      style={{ width: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)' }}
                    />
                    <button
                      type="button"
                      className="btn-cancel"
                      style={{ width: '100%', marginTop: 8, padding: '6px 12px', fontSize: '0.78rem' }}
                      onClick={() => {
                        const input = document.getElementById('verso-file-input');
                        if (input) input.click();
                      }}
                    >
                      Substituir Verso
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 180, width: '100%' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>Nenhuma imagem carregada</span>
                    <button
                      type="button"
                      className="btn-save"
                      style={{ padding: '6px 14px', fontSize: '0.78rem' }}
                      onClick={() => {
                        const input = document.getElementById('verso-file-input');
                        if (input) input.click();
                      }}
                    >
                      Selecionar Arquivo
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  id="verso-file-input"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setRedactorFile(file);
                      setRedactorTitle('Verso');
                    }
                    e.target.value = '';
                  }}
                />
              </div>
            </div>

            {/* If there are changes to upload, show save button */}
            {auditUser && (docFrenteBase64 !== (auditUser.documentoFrente || '') || docVersoBase64 !== (auditUser.documentoVerso || '')) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button
                  type="button"
                  className="btn-save"
                  disabled={submittingDoc || !docFrenteBase64 || !docVersoBase64}
                  onClick={async () => {
                    if (!auditUser) return;
                    setSubmittingDoc(true);
                    try {
                      await api.post(`/upload/documentos-usuario/${auditUser.id}`, {
                        documentoFrente: docFrenteBase64,
                        documentoVerso: docVersoBase64
                      });
                      toast.success('Documentos enviados com sucesso!');
                      refetch();
                      setAuditUser(null);
                    } catch (err: any) {
                      toast.error(err.response?.data?.message || 'Erro ao enviar documentos.');
                    } finally {
                      setSubmittingDoc(false);
                    }
                  }}
                >
                  {submittingDoc ? 'Enviando...' : 'Salvar e Enviar para Avaliação'}
                </button>
              </div>
            )}

            {/* Moderation Panel for Master Admin */}
            {auditUser && currentUser?.perfil === 'ADMIN' && auditUser.documentoStatus === 'PENDENTE' && (
              <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 16, marginTop: 16 }}>
                <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: 12 }}>Painel de Moderação (Master Admin)</h3>

                {!showRejectionInput ? (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="button"
                      className="btn-save"
                      style={{ background: '#10b981', borderColor: '#10b981' }}
                      disabled={submittingDoc}
                      onClick={async () => {
                        if (!auditUser) return;
                        setSubmittingDoc(true);
                        try {
                          await api.post(`/upload/documento-decidir/${auditUser.id}`, { status: 'APROVADO' });
                          toast.success('Documento aprovado!');
                          refetch();
                          setAuditUser(null);
                        } catch (err: any) {
                          toast.error(err.response?.data?.message || 'Erro ao decidir status.');
                        } finally {
                          setSubmittingDoc(false);
                        }
                      }}
                    >
                      Aprovar Documentos
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      style={{ background: '#ef4444', color: 'white', borderColor: '#ef4444' }}
                      onClick={() => setShowRejectionInput(true)}
                    >
                      Rejeitar...
                    </button>
                  </div>
                ) : (
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label className="form-label" style={{ color: '#fca5a5' }}>Motivo da Rejeição *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ex: CPF ou data de nascimento ilegível."
                      value={rejectionReason}
                      onChange={e => setRejectionReason(e.target.value)}
                    />
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={() => {
                          setShowRejectionInput(false);
                          setRejectionReason('');
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn-save"
                        style={{ background: '#ef4444', borderColor: '#ef4444' }}
                        disabled={submittingDoc || !rejectionReason.trim()}
                        onClick={async () => {
                          if (!auditUser || !rejectionReason.trim()) return;
                          setSubmittingDoc(true);
                          try {
                            await api.post(`/upload/documento-decidir/${auditUser.id}`, {
                              status: 'REJEITADO',
                              motivo: rejectionReason
                            });
                            toast.success('Documento rejeitado!');
                            refetch();
                            setAuditUser(null);
                          } catch (err: any) {
                            toast.error(err.response?.data?.message || 'Erro ao decidir status.');
                          } finally {
                            setSubmittingDoc(false);
                          }
                        }}
                      >
                        Confirmar Rejeição
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 24px 20px' }}>
            <button className="btn-cancel" onClick={() => setAuditUser(null)}>Fechar</button>
          </div>
        </div>
      </Modal>

      {redactorFile && (
        <DocumentRedactorModal
          file={redactorFile}
          title={redactorTitle}
          onCompleted={(base64) => {
            if (redactorTitle === 'Frente') {
              setDocFrenteBase64(base64);
            } else {
              setDocVersoBase64(base64);
            }
            setRedactorFile(null);
          }}
          onCancel={() => setRedactorFile(null)}
        />
      )}

      {/* Modal de Detalhes do Usuário */}
      <Modal
        open={!!detailUser}
        onClose={() => setDetailUser(null)}
        title="Ficha Detalhada do Usuário"
        size="lg"
      >
        {detailUser && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, background: 'var(--bg-muted, #f8fafc)', borderRadius: 12, border: '1px solid var(--border-color, #e2e8f0)' }}>
              <UserAvatar src={(detailUser as any).fotoUrl} name={detailUser.nome} role={detailUser.perfil} size={48} />
              <div>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-main)' }}>{detailUser.nome}</h3>
                <p style={{ margin: '2px 0 0', fontSize: '.82rem', color: 'var(--text-muted)' }}>{detailUser.email}</p>
                <span style={{ display: 'inline-block', marginTop: 6, padding: '2px 8px', borderRadius: 6, fontSize: '.72rem', fontWeight: 700, background: '#6366f120', color: '#6366f1' }}>
                  {detailUser.perfil}
                </span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: '.85rem' }}>
              <div style={{ padding: 12, background: 'var(--bg-muted, #f9fafb)', borderRadius: 8 }}>
                <span style={{ color: '#6b7280', fontSize: '.75rem', fontWeight: 600, display: 'block' }}>CPF</span>
                <strong>
                  {detailUser.cpf
                    ? formatCPF(detailUser.cpf)
                    : ((detailUser as any).responsavelVinculado?.cpf
                        ? formatCPF((detailUser as any).responsavelVinculado.cpf)
                        : 'Não informado')}
                </strong>
              </div>
              <div style={{ padding: 12, background: 'var(--bg-muted, #f9fafb)', borderRadius: 8 }}>
                <span style={{ color: '#6b7280', fontSize: '.75rem', fontWeight: 600, display: 'block' }}>Data de Nascimento</span>
                <strong>
                  {detailUser.dataNascimento
                    ? new Date(detailUser.dataNascimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                    : ((detailUser as any).responsavelVinculado?.dataNascimento
                        ? new Date((detailUser as any).responsavelVinculado.dataNascimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                        : 'Não informada')}
                </strong>
              </div>
              <div style={{ padding: 12, background: 'var(--bg-muted, #f9fafb)', borderRadius: 8 }}>
                <span style={{ color: '#6b7280', fontSize: '.75rem', fontWeight: 600, display: 'block' }}>Telefone / WhatsApp</span>
                <strong>{detailUser.telefone || (detailUser as any).responsavelVinculado?.telefone || 'Não informado'}</strong>
              </div>
              <div style={{ padding: 12, background: 'var(--bg-muted, #f9fafb)', borderRadius: 8 }}>
                <span style={{ color: '#6b7280', fontSize: '.75rem', fontWeight: 600, display: 'block' }}>Status da Conta</span>
                <strong style={{ color: detailUser.ativo ? '#10b981' : '#ef4444' }}>{detailUser.ativo ? 'Ativo' : 'Inativo'} ({detailUser.statusConta || 'Sem status'})</strong>
              </div>
              <div style={{ padding: 12, background: 'var(--bg-muted, #f9fafb)', borderRadius: 8 }}>
                <span style={{ color: '#6b7280', fontSize: '.75rem', fontWeight: 600, display: 'block' }}>Código de Ativação</span>
                <code style={{ color: '#6366f1', fontWeight: 700 }}>{(detailUser as any).codigoDisplay || 'N/A'}</code>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn-cancel" onClick={() => setDetailUser(null)}>Fechar</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Confirmação com Senha de Segurança para Exclusão Definitiva */}
      <PasswordConfirmModal
        open={!!userToHardDelete}
        title="Excluir Usuário Definitivamente"
        description={`Para excluir permanentemente o usuário "${userToHardDelete?.nome}" do banco de dados, digite a palavra-chave de autorização:`}
        requiredPassword="marcelo"
        confirmLabel="Excluir Usuário"
        onCancel={() => setUserToHardDelete(null)}
        onConfirm={confirmHardDeleteUser}
      />

      {/* Modal Informativo: O que é Cadastro Escolar */}
      {infoCadastroEscolarTipo && (
        <CadastroEscolarInfoModal
          open={!!infoCadastroEscolarTipo}
          tipo={infoCadastroEscolarTipo === 'geral' ? 'aluno' : infoCadastroEscolarTipo}
          onClose={() => setInfoCadastroEscolarTipo(null)}
        />
      )}

      {/* Modal Ficha do Aluno — Portal no body */}
      {fichaAlunoId && typeof document !== 'undefined' && createPortal(
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setFichaAlunoId(null);
          }}
        >
          <div
            style={{
              width: '96vw',
              maxWidth: 1280,
              height: '92vh',
              maxHeight: 900,
              background: '#ffffff',
              borderRadius: 14,
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <AlunoFichaDesktop
              alunoId={fichaAlunoId}
              escolaId={escolaFichaId || ''}
              canManage={true}
              onClose={() => setFichaAlunoId(null)}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Modal Ficha do Responsável — Portal no body */}
      {fichaResp && typeof document !== 'undefined' && createPortal(
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setFichaResp(null);
          }}
        >
          <div
            style={{
              width: '96vw',
              maxWidth: 1100,
              maxHeight: '92vh',
              background: '#ffffff',
              borderRadius: 14,
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <ResponsavelFichaDesktop
              responsavelId={fichaResp.id}
              escolaId={fichaResp.escolaId}
              canManage={false}
              onClose={() => setFichaResp(null)}
              onEdit={() => {}}
              onDeactivate={() => {}}
              onReactivate={() => {}}
              canDeactivate={{ ok: false }}
              onInvited={() => { void refetch(); }}
              onOpenAluno={(alunoId) => {
                setFichaAlunoId(alunoId);
                setEscolaFichaId(fichaResp.escolaId);
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
